import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Trash2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const DataDeletion = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !reason) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    try {
      // Create mailto link with pre-filled content
      const subject = encodeURIComponent('Data Deletion Request');
      const body = encodeURIComponent(
        `Deletion Request from ${email}\n\nReason:\n${reason}\n\nPlease process my data deletion request within 30 days as per the Data Privacy Act.`
      );
      const mailtoLink = `mailto:support@boholtrabaho.com?subject=${subject}&body=${body}`;
      
      // Open email client
      window.location.href = mailtoLink;
      
      // Show success message
      toast.success('Request sent — we\'ll process in 30 days.', {
        description: 'Your email client should open. Please send the email to complete your request.'
      });
      
      // Clear form
      setEmail('');
      setReason('');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create request. Please email support@boholtrabaho.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" className="mb-6" data-testid="back-button">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Button>
        </Link>

        <Card data-testid="data-deletion-card">
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-2xl font-heading">Request Data Deletion</CardTitle>
                <CardDescription className="mt-2">
                  Submit a request to delete your personal data from Bohol Trabaho
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="deletion-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the email associated with your account
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Deletion *</Label>
                <Textarea
                  id="reason"
                  placeholder="Please tell us why you want to delete your data..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={5}
                  required
                  data-testid="deletion-reason-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  This helps us improve our service
                </p>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h4 className="font-semibold text-sm mb-2 text-yellow-900 dark:text-yellow-100">
                  Important Information
                </h4>
                <ul className="text-xs text-yellow-800 dark:text-yellow-200 space-y-1 list-disc list-inside">
                  <li>Data deletion is permanent and cannot be undone</li>
                  <li>We will process your request within 30 days</li>
                  <li>You will receive a confirmation email</li>
                  <li>Some data may be retained for legal compliance</li>
                </ul>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
                data-testid="submit-deletion-button"
              >
                <Mail size={16} className="mr-2" />
                {submitting ? 'Sending...' : 'Submit Deletion Request'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By submitting this request, you confirm that you want to permanently delete your data.
              </p>
            </form>

            <div className="mt-6 pt-6 border-t">
              <p className="text-xs text-muted-foreground">
                Need help? Contact us at{' '}
                <a href="mailto:support@boholtrabaho.com" className="text-primary hover:underline">
                  support@boholtrabaho.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataDeletion;